import { useAsync, useAsyncFn } from 'react-use-v13';
import apiFetch, { defaultApiFetchErrorHandler } from '@scale/frontend-lib/api-fetch';
import { useState } from 'react';
import _ from 'lodash';

const useAuditErrorCategories = (projectId: string) => {
  const [errorCategories, setErrorCategories] = useState<string[]>([]);

  useAsync(async () => {
    // 1. Support fetching the categories. But cache them. This cache also gets updated when we mae
    // an update
    const { errorCategories } = await apiFetch(
      null,
      `corp-api/attemptAudit/errorCategories/${projectId}`,
    );

    // We set the error categories manually instead of using native `useAsync` API because we _also_ need
    // to set this value when we update error categories, and we can't use this API
    // a second time for that because of the config cache update delay.
    setErrorCategories(errorCategories);
  });
  const [{ loading }, updateAuditErrorCategories] = useAsyncFn(
    async (newErrorCategories?: string[]) => {
      if (
        !newErrorCategories &&
        Array.isArray(newErrorCategories) &&
        _.every(newErrorCategories, x => typeof x === 'string')
      )
        return;
      const { errorCategories } = await apiFetch<{ errorCategories: string[] }>(
        null,
        `corp-api/attemptAudit/errorCategories/${projectId}`,
        {
          method: 'POST',
          body: { errorCategories: newErrorCategories },
        },
      ).catch(e => {
        defaultApiFetchErrorHandler(e, { handleHttp: true });
        throw e;
      });
      setErrorCategories(errorCategories);
    },
    [setErrorCategories],
  );

  const addNewCategory = (category: string) => {
    return updateAuditErrorCategories([...new Set(errorCategories.concat(category))]);
  };

  const removeNewCategory = (category: string) => {
    return updateAuditErrorCategories([
      ...new Set(errorCategories.filter(error => error !== category)),
    ]);
  };

  return {
    errorCategories,
    updateAuditErrorCategories,
    loading,
    addNewCategory,
    removeNewCategory,
  };
};

export default useAuditErrorCategories;
